import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../utils/notificationService';
import { generateSmartNotifications, dismissNotificationType, markNotificationAsRead as markSmartNotificationRead } from '../utils/notificationManager';
import { useNavigate } from 'react-router-dom';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadNotifications();
    
    // Refresh notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) return;

    // Get forum notifications from Supabase
    const forumResult = await getNotifications(userEmail, false);
    const forumNotifs = forumResult.success ? forumResult.data.map(n => ({
      ...n,
      source: 'forum',
      read: n.is_read
    })) : [];

    // Get smart reminders from notificationManager
    const smartNotifs = generateSmartNotifications().map(n => ({
      ...n,
      source: 'smart',
      created_at: new Date().toISOString(),
      read: n.read || false
    }));

    // Combine and sort by date (newest first)
    const combined = [...forumNotifs, ...smartNotifs].sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    );

    setNotifications(combined);
    setUnreadCount(combined.filter(n => !n.read).length);
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read based on source
    if (!notification.read) {
      if (notification.source === 'forum') {
        await markNotificationAsRead(notification.id);
      } else if (notification.source === 'smart') {
        markSmartNotificationRead(notification.type);
      }
      
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    // Navigate to the link if provided
    const link = notification.link || notification.action?.link;
    if (link) {
      setShowDropdown(false);
      navigate(link);
    }
  };

  const handleMarkAllRead = async () => {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) return;

    setLoading(true);
    
    // Mark all forum notifications as read in Supabase
    await markAllNotificationsAsRead(userEmail);
    
    // Mark all smart notifications as read in localStorage
    notifications.forEach(n => {
      if (n.source === 'smart') {
        markSmartNotificationRead(n.type);
      }
    });
    
    setNotifications(prev => prev.map(n => ({ ...n, read: true, is_read: true })));
    setUnreadCount(0);
    setLoading(false);
  };

  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    
    // Check if notification was unread before deleting
    const notification = notifications.find(n => n.id === notificationId);
    const wasUnread = notification && !notification.read;
    
    // Delete based on source
    if (notification.source === 'forum') {
      await deleteNotification(notificationId);
    } else if (notification.source === 'smart') {
      dismissNotificationType(notification.type);
    }
    
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    
    // Only decrement unread count if it was actually unread
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      // Forum notifications
      case 'new_post': return '💬';
      case 'post_like': return '❤️';
      case 'comment': return '💭';
      case 'admin_post': return '📢';
      // Smart reminders
      case 'streak': return '🔥';
      case 'weight': return '⚖️';
      case 'reflection': return '📝';
      case 'goal': return '🎯';
      case 'recipe': return '🍽️';
      case 'community': return '💬';
      case 'journal': return '📝';
      case 'event': return '🎯';
      case 'newProgram': return '🆕';
      case 'milestone': return '🎉';
      case 'cycle': return '🌸';
      case 'resource': return '📚';
      default: return '🔔';
    }
  };

  const timeAgo = (timestamp) => {
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div className="relative">
      {/* Bell Icon */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setShowDropdown(false)}
            />
            
            {/* Notification Panel */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed sm:absolute left-4 right-4 sm:right-0 sm:left-auto top-16 sm:top-auto mt-0 sm:mt-2 w-auto sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-[500px] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <h3 className="font-semibold text-gray-800">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    disabled={loading}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto flex-1">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="text-4xl mb-2">🔔</div>
                    <p className="text-sm">No notifications yet</p>
                  </div>
                ) : (
                  notifications.map(notification => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        {/* Icon */}
                        <div className="flex-shrink-0 text-2xl">
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {notification.source === 'smart' && notification.time ? notification.time : timeAgo(notification.created_at)}
                          </p>
                        </div>

                        {/* Delete Button */}
                        <button
                          onClick={(e) => handleDelete(notification.id, e)}
                          className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>

                        {/* Unread Indicator */}
                        {!notification.read && (
                          <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
